import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Sparkles,
  MessageSquareText,
  Camera,
  HeartHandshake,
  ArrowRight,
  Phone,
  BookOpenCheck,
} from "lucide-react";
import VerificationTool from "@/components/VerificationTool";
import ShareSection from "@/components/ShareSection";
import SEO from "@/components/SEO";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_40ad2524-223c-429c-8f1d-4ea2ecf9a2d3/artifacts/6n4i34v7_logo.png";

export default function Home() {
  return (
    <div data-testid="home-page">
      <SEO
        path="/"
        title=""
        description="Verifica truffa online gratis: incolla un SMS sospetto, una email strana o uno screenshot WhatsApp e scopri in pochi secondi se è una truffa o phishing. Anonimo, in italiano semplice, pensato per anziani e famiglie."
      />
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#DBEAFE] via-white to-white">
        <div className="absolute inset-0 pointer-events-none opacity-50">
          <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#22C55E]/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-[#1E3A8A]/10 blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="animate-fade-up">
              <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#22C55E]/15 text-[#16A34A] text-base font-bold mb-6">
                <Sparkles size={20} /> Gratuito e Sicuro
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#1E3A8A] leading-[1.05]">
                Hai ricevuto un messaggio strano?{" "}
                <span className="text-[#22C55E]">Te lo controlliamo noi.</span>
              </h1>
              <p className="mt-6 text-xl md:text-2xl text-gray-700 leading-relaxed">
                Incolla il testo o carica una foto del messaggio.
                Il nostro assistente intelligente ti dice subito se è una
                <span className="font-bold text-[#DC2626]"> truffa </span>
                o un messaggio
                <span className="font-bold text-[#16A34A]"> vero</span>.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a
                  href="#verifica"
                  data-testid="hero-cta-verifica"
                  className="bg-[#1E3A8A] hover:bg-[#152C69] text-white min-h-[64px] px-8 py-4 rounded-2xl shadow-lg hover:shadow-xl transition-all active:scale-95 flex items-center justify-center gap-3 text-xl font-bold"
                >
                  <ShieldCheck size={26} /> Verifica un messaggio
                </a>
                <Link
                  to="/guida"
                  data-testid="hero-cta-guida"
                  className="border-4 border-[#1E3A8A] text-[#1E3A8A] bg-white hover:bg-blue-50 min-h-[64px] px-8 py-4 rounded-2xl transition-all flex items-center justify-center gap-3 text-xl font-bold"
                >
                  <BookOpenCheck size={26} /> Leggi la Guida
                </Link>
              </div>

              <div className="mt-10 flex items-center gap-6 text-gray-600">
                <div className="flex -space-x-3">
                  <div className="w-12 h-12 rounded-full bg-[#22C55E] flex items-center justify-center text-white font-bold border-4 border-white">
                    ✓
                  </div>
                  <div className="w-12 h-12 rounded-full bg-[#1E3A8A] flex items-center justify-center text-white font-bold border-4 border-white">
                    ⚡
                  </div>
                  <div className="w-12 h-12 rounded-full bg-[#DC2626] flex items-center justify-center text-white font-bold border-4 border-white">
                    !
                  </div>
                </div>
                <div className="text-base md:text-lg">
                  <strong>Pronto in 10 secondi.</strong> Risposta semplice e chiara.
                </div>
              </div>
            </div>

            <div className="relative animate-fade-up">
              <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl border-8 border-white">
                <img
                  src="https://images.pexels.com/photos/6787953/pexels-photo-6787953.jpeg"
                  alt="Nonni che usano un tablet con serenità"
                  fetchPriority="high"
                  className="w-full h-[420px] lg:h-[560px] object-cover"
                />
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md rounded-2xl p-5 shadow-xl flex items-center gap-4">
                  <img src={LOGO_URL} alt="ScudoNonni" className="h-16 w-auto" />
                  <div>
                    <div className="font-heading text-xl font-extrabold text-[#1E3A8A]">
                      Ti protegge, ti fa stare sereno
                    </div>
                    <div className="text-base text-gray-600">
                      Come avere un nipote esperto sempre vicino
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="text-center mb-14">
          <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-[#1E3A8A]">
            Come funziona, passo dopo passo
          </h2>
          <p className="mt-4 text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto">
            Tre semplici passi per scoprire se un messaggio è davvero da fidarsi.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              n: 1,
              icon: MessageSquareText,
              title: "Incolla il testo o carica la foto",
              desc: "Copia il messaggio sospetto oppure fai uno screenshot e caricalo qui. Più semplice di così non si può.",
            },
            {
              n: 2,
              icon: Sparkles,
              title: "Il nostro assistente lo analizza",
              desc: "In pochi secondi, controlliamo segnali di pericolo, parole comuni delle truffe e link sospetti.",
            },
            {
              n: 3,
              icon: ShieldCheck,
              title: "Ricevi la risposta chiara",
              desc: "Ti diciamo se è SICURO, SOSPETTO o una TRUFFA, con consigli su cosa fare subito.",
            },
          ].map((s) => (
            <div
              key={s.n}
              data-testid={`how-step-${s.n}`}
              className="bg-white p-8 rounded-3xl shadow-lg border-2 border-gray-100 hover:border-blue-200 hover:shadow-xl transition-all"
            >
              <div className="w-16 h-16 rounded-2xl bg-[#DBEAFE] flex items-center justify-center text-[#1E3A8A] mb-5">
                <s.icon size={36} />
              </div>
              <div className="text-sm font-bold text-[#22C55E] mb-2">
                PASSO {s.n}
              </div>
              <h3 className="font-heading text-2xl font-bold text-[#1E3A8A] mb-3">
                {s.title}
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VERIFICATION TOOL */}
      <section
        id="verifica"
        className="bg-gradient-to-b from-[#FAFAFA] to-[#DBEAFE]/40 py-16 md:py-24"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1E3A8A] text-white text-base font-bold mb-5">
              <ShieldCheck size={20} /> Strumento di verifica
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-[#1E3A8A]">
              Verifica un messaggio ora
            </h2>
            <p className="mt-4 text-xl text-gray-700 max-w-2xl mx-auto">
              Scegli se incollare il testo oppure caricare una foto del messaggio.
            </p>
          </div>
          <VerificationTool />
        </div>
      </section>

      {/* QUICK TIPS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-[2.5rem] overflow-hidden shadow-2xl border-8 border-white">
            <img
              src="https://images.pexels.com/photos/8949842/pexels-photo-8949842.jpeg"
              alt="Coppia di anziani che usano un tablet con serenità"
              loading="lazy"
              className="w-full h-[420px] object-cover"
            />
          </div>
          <div>
            <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-[#1E3A8A] leading-tight">
              Regole d'oro per non cadere nella rete
            </h2>
            <p className="mt-5 text-xl text-gray-700">
              Le truffe online sembrano vere, ma seguono sempre schemi simili. Memorizza queste regole semplici:
            </p>
            <ul className="mt-6 space-y-4">
              {[
                "Non cliccare mai su link strani, neanche se sembrano della tua banca",
                "Mai dare codici, password o dati bancari per telefono o messaggio",
                "Diffida di chi ti mette fretta o promette premi",
                "Nel dubbio, chiama un familiare o usa ScudoNonni",
              ].map((t, i) => (
                <li
                  key={t}
                  data-testid={`tip-row-${i}`}
                  className="flex items-start gap-4 text-lg md:text-xl"
                >
                  <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[#22C55E] text-white flex items-center justify-center font-bold">
                    ✓
                  </span>
                  <span className="text-gray-800">{t}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/guida"
              data-testid="cta-leggi-guida"
              className="mt-8 inline-flex items-center gap-3 bg-[#22C55E] hover:bg-[#16A34A] text-white px-8 py-4 rounded-2xl text-xl font-bold shadow-lg hover:shadow-xl transition-all"
            >
              Leggi tutti i consigli <ArrowRight size={22} />
            </Link>
          </div>
        </div>
      </section>

      {/* USEFUL NUMBERS CTA */}
      <section className="bg-[#1E3A8A] text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-white/15 text-white text-base font-bold mb-5">
              <Phone size={20} /> Numeri Utili
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-extrabold leading-tight">
              In caso di truffa, sai sempre chi chiamare
            </h2>
            <p className="mt-5 text-xl text-blue-100">
              Polizia Postale, Carabinieri, antitruffa anziani, banca. Tutti i numeri importanti in un'unica pagina semplice.
            </p>
            <Link
              to="/numeri-utili"
              data-testid="cta-numeri-utili"
              className="mt-8 inline-flex items-center gap-3 bg-[#22C55E] hover:bg-[#16A34A] text-white px-8 py-4 rounded-2xl text-xl font-bold shadow-lg hover:shadow-xl transition-all"
            >
              Vedi i Numeri Utili <ArrowRight size={22} />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: "Emergenze", num: "112" },
              { label: "Polizia di Stato", num: "113" },
              { label: "Polizia di Stato (centralino)", num: "06 46541" },
              { label: "Auser Filo d'Argento", num: "800 995 988" },
            ].map((n) => (
              <div
                key={n.label}
                data-testid={`useful-number-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className="bg-white/10 hover:bg-white/15 rounded-2xl p-5 transition-colors"
              >
                <div className="text-sm text-blue-200 font-semibold uppercase tracking-wider">
                  {n.label}
                </div>
                <div className="text-2xl md:text-3xl font-extrabold mt-1">
                  {n.num}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 text-center">
        <HeartHandshake size={64} className="mx-auto text-[#22C55E] mb-6" />
        <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-[#1E3A8A]">
          Un servizio fatto con il cuore, per i nonni
        </h2>
        <p className="mt-5 text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
          ScudoNonni nasce dall'idea che nessun nonno dovrebbe perdere i suoi
          risparmi o la sua serenità a causa di una truffa. È gratis, non chiede dati,
          ed è qui per aiutarti — come farebbe un nipote che ne sa.
        </p>
      </section>

      {/* SHARE */}
      <ShareSection />
    </div>
  );
}
